import { Box, Button, FormControl, Input, InputAdornment, InputLabel, Modal, Paper, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, tooltipClasses, Typography, IconButton, CircularProgress, Snackbar, Alert } from "@mui/material";
import React, { useEffect, useState } from "react";
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { getUser } from "../../utils/authService";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from "react-router-dom";
import { getSavedFilesFromDB, getResultsFromDB } from '../../utils/helpers';


const CostumTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FCF8F3',
    color: '#7e3e1b',
    maxWidth: "10vw",
    border: '2px solid #7e3e1b',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#7e3e1b',
  },
}));

const ScansTable = (props) => {
  const [data, setData] = useState([]);
  const [filename, setFilename] = useState("");
  const [openFilenameModal, setOpenFilenameModal] = useState(false);
  const [savedFiles, setSavedFiles] = useState(null);
  const [asc, setAsc] = useState(false);
  const [row, setRow] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setData(props.scanData);
    sortByDate();
  }, []);

  useEffect(() => {
    const email = getUser();
    if (!email) {
      navigate("/login");
    }
    getSavedFilesFromDB(email).then((res) => {
      setSavedFiles(res);
    });
  }, []);

  const handleDeleteFileModal = (fileIdentifier) => {
    setFileToDelete(fileIdentifier);
    setDeleteConfirmOpen(true);
  }

  const confirmDeleteFile = () => {
    const email = getUser();
    setIsDeleting(true);

    fetch(`https://server.nepeto.com/aniseller_delete_result/${email}/${fileToDelete}/`, { method: "DELETE" })
      .then((res) => {
        setIsDeleting(false);
        if (res.ok) {
          setData(data.filter(item => item.fileIdentifier !== fileToDelete));
          setDeleteConfirmOpen(false);
          setFileToDelete(null);
          setDeleteError(null);
          setDeleteSuccess(true);
        } else {
          setIsDeleting(false);
          setDeleteError(<React.Fragment>Failed to delete file.<br />Please try again later.</React.Fragment>);
        }
      })
      .catch((err) => {
        setIsDeleting(false);
        setDeleteError(<React.Fragment>Failed to delete file.<br />Please try again later.</React.Fragment>);
      });
  }

  const cancelDeleteFile = () => {
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
    setDeleteError(null);
  }

  const handleCloseSuccess = (event, reason) => {
    if (reason === 'clickaway') { return; }
    setDeleteSuccess(false);
  };

  function loadSavedFile(fileIdentifier) {
    const email = getUser();
    if (!email) {
      navigate("/login");
    }
    getResultsFromDB(email, fileIdentifier).then((res) => {
      if (res) {
        navigate('/csvscan', {
          state: {
            selectedStore: res[0].selectedStore,
            identifier: res[0].identifier,
            data: JSON.parse(res[0].data || "[]"),
            filters: JSON.parse(res[0].filters || "{}"),
            loadedFile: true
          }
        })
      }
    });
  }


  function parseLocaleDate(dateString) {
    const [datePart, timePart] = dateString.split(', '); // Split date and time

    let day, month, year;
    if (datePart.includes("/")) {
      [month, day, year] = datePart.split('/').map(Number); // MM/DD/YYYY
    } else {
      [day, month, year] = datePart.split('.').map(Number); // DD.MM.YYYY
    }

    const [hours, minutes, seconds] = timePart.replace(" PM", "").replace(" AM", "").split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes, seconds);
  }

  const sortByDate = () => {
    setAsc(!asc);
    const sortedData = [...props.scanData].sort((a, b) => {
      const dateA = parseLocaleDate(a.date);
      const dateB = parseLocaleDate(b.date);

      if (asc) {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });

    setData(sortedData);
  };

  return (
    <>
      <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'background.paper', maxHeight: '50vh', }}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell align="left" style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '4px' }} onClick={() => sortByDate()}>
                Date{asc ? <ArrowDownwardOutlinedIcon /> : <ArrowUpwardOutlinedIcon />}
              </TableCell>
              <TableCell align="left">Line Count</TableCell>
              <TableCell align="center">Open</TableCell>
              <TableCell align="center">Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data && data.map((row, index) => (
              <TableRow key={index}>
                <TableCell scope="row" style={{ display: 'flex', alignItems: 'center' }}>
                  <img src={`../../assets/${row.fileName.endsWith('.csv') ? 'csv' : 'xlsx'}.png`} width={'35vw'} />&nbsp;{row.fileName}
                </TableCell>
                <TableCell align="left">{row.date}</TableCell>
                <TableCell align="left">{row.lineCount}</TableCell>
                {savedFiles === null ? <TableCell align="center">Loading...</TableCell> : savedFiles.includes(row.fileIdentifier) ? (
                  <TableCell align="center">
                    <Button
                      onClick={() => loadSavedFile(row.fileIdentifier)}
                      sx={{
                        width: 50,
                        height: 50,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: 0,
                        minWidth: "auto",
                        borderRadius: "50%",
                        mx: "auto",
                      }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 35 }} />
                    </Button>
                  </TableCell>
                ) : <TableCell align="center">Not Saved</TableCell>}
                <TableCell>
                  <IconButton sx={{
                    width: 50,
                    height: 50,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 0,
                    minWidth: "auto",
                    borderRadius: "50%",
                    mx: "auto",
                    color: "#7e3e1b",
                  }} onClick={() => handleDeleteFileModal(row.fileIdentifier)}>
                    <DeleteOutlineIcon sx={{fontSize: 35}}/>
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        open={deleteConfirmOpen}
        onClose={!isDeleting ? cancelDeleteFile : undefined}
        aria-labelledby="delete-confirmation-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="delete-confirmation-title" variant="h6" component="h2" align="center">
            {deleteError ? "Error" : "Confirm Deletion"}
          </Typography>
          <Typography id="delete-confirmation-description" sx={{ mt: 2 }} align="center" color={deleteError ? "error" : "inherit"}>
            {deleteError ? deleteError :
              <>
                Are you sure you want to delete this file?<br />
                This action cannot be undone.
              </>
            }
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 3 }}>
            {isDeleting ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', my: 1 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Button
                  onClick={cancelDeleteFile}
                  variant="outlined"
                  sx={{
                    height: 50,
                    width: 88,
                    minWidth: 'unset',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                  disabled={isDeleting}
                >
                  {deleteError ? "Close" : "Cancel"}
                </Button>
                {!deleteError && (
                  <Button
                    onClick={confirmDeleteFile}
                    variant="contained"
                    color="error"
                    sx={{ width: 88 }}
                    disabled={isDeleting}
                  >
                    Delete
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Success notification */}
      <Snackbar
        open={deleteSuccess}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSuccess}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          File deleted successfully
        </Alert>
      </Snackbar>
    </>
  );
};

export default ScansTable;
